import { useSceneEvents } from "@/components/common/scene-events-context";
import { selectCompareElement } from "@/store/modes/walk-mode-slice";
import { selectActiveElementId } from "@/store/selections-selectors";
import type { RootState } from "@/store/store";
import { CameraView } from "@/utils/cam-view";
import type { TypedEvent } from "@faro-lotv/foundation";
import type { GUID } from "@faro-lotv/ielement-types";

/** The event moving the camera of one view, and the element it is meant for */
type CameraViewEvent = {
  /** The event with the pose to animate the camera to */
  setCameraView: TypedEvent<CameraView>;

  /**
   * @param state The current state
   * @returns the element of this view the pose is meant for
   */
  selectTargetId(state: RootState): GUID | undefined;
};

/**
 * @param isRightView Whether the scene is the right view of split mode
 * @returns the event moving the camera of this view: the right view of split mode
 * follows its own event and compare element, so the left view's pose is not
 * applied to it while both show the same element
 */
export function useCameraViewEvent(isRightView: boolean): CameraViewEvent {
  const { setCameraView, setRightCameraView } = useSceneEvents();
  return isRightView
    ? { setCameraView: setRightCameraView, selectTargetId: selectCompareId }
    : { setCameraView, selectTargetId: selectActiveElementId };
}

function selectCompareId(state: RootState): GUID | undefined {
  return selectCompareElement(state)?.id;
}
